/*
 * Accepted options:
 * - playAfterSeek: boolean (default: false)
 */
export const setPlayerCurrentTime = (player, time, options) => {
  options = options || {}
  player._userSeeked = true
  const hasNotPlayed = player.readyState() === 0
  // e.g. when we click a transcript line, we want the player to move to that position.
  // If the player has not played, the metadata for the player is not loaded and nothing happens.
  // In this case, the following code will start/play the content.
  // If player reaches the 'playing' event, it means it loaded the metadata, and also fetched previous 'current position'
  // So after that is when we can/should update the currentTime, and pause it (because it was paused before; unless we want to playAfterSeek now)
  if (hasNotPlayed) {
    player.ready()
    player.play()

    const updateTime = () => {
      player.currentTime(time)
      if (!options.playAfterSeek) player.pause()
      player.off('playing', updateTime)
    }
    player.on('playing', updateTime)
  } else {
    player.currentTime(time)
    if (options.playAfterSeek) player.play()
  }
  player.el().focus({ preventScroll: true }) // Focus the player so people can control it via keyboard shortcuts
}

export async function waitForPlayer(videoElement) {
  let player = window.videojs?.getPlayer(videoElement)
  if (player) return player

  return new Promise(function (resolve) {
    videoElement.addEventListener('simplero:media:ready', () => {
      resolve(window.videojs.getPlayer(videoElement))
    })
  })
}

// Reloading the source during recovery resets the playback rate to the default and fires
// ratechange. Rate listeners check this to skip that until resetPlayer has restored the rate.
export const isRecoveringPlayer = (player) => Boolean(player.beforeReset)

// Calls onChange with the new rate for every ratechange that reflects the user's choice, skipping
// the default rate a recovery reload fires (resetPlayer restores the real rate afterwards).
export function onUserRateChange(player, onChange) {
  player.on('ratechange', () => {
    if (isRecoveringPlayer(player)) return
    onChange(player.playbackRate())
  })
}

export function resetPlayer(player) {
  // restart-when-stalled.js and error-handler.js each register their own 'online' listener
  // and can both decide to recover on the same reconnect event, calling this synchronously
  // back to back. Coalesce same-tick re-entry into a single reset; a call arriving after the
  // microtask below still runs normally (a genuinely later stall/reconnect must still recover).
  if (player._resetPlayerPending) return
  player._resetPlayerPending = true
  Promise.resolve().then(() => {
    player._resetPlayerPending = false
  })

  const curTime = player.beforeReset?.currentTime ?? player.currentTime()
  const curSource = player.beforeReset?.currentSource ?? player.currentSource() // {src, type, …}
  const rate = player.beforeReset?.playbackRate ?? player.playbackRate()

  player.beforeReset = {
    currentTime: curTime,
    currentSource: curSource,
    playbackRate: rate,
  }

  // Clear any existing reset metadata timer/listener to avoid duplicate triggers
  if (player._resetMetadataTimeout) {
    clearTimeout(player._resetMetadataTimeout)
    player._resetMetadataTimeout = null
  }
  if (player._resetMetadataHandler) {
    player.off('loadedmetadata', player._resetMetadataHandler)
    player._resetMetadataHandler = null
  }

  // Reloading the source pauses the element without a 'pause' event, so video.js would keep
  // showing the player as playing if the play() below is refused. Pause explicitly first.
  if (
    typeof player.pause === 'function' &&
    typeof player.paused === 'function' &&
    !player.paused()
  ) {
    player.pause()
  }

  // Don't call player.reset(): in video.js 7.20.3 it builds a brand-new <video> element, and
  // iOS refuses programmatic play() on an element the user never tapped, so the video stays
  // paused. src() reloads the existing element (and replaces a hung VHS source handler).
  if (curSource) {
    player.src(curSource)
  }

  player._resetMetadataHandler = function () {
    if (player._resetMetadataTimeout) {
      clearTimeout(player._resetMetadataTimeout)
      player._resetMetadataTimeout = null
    }
    player._resetMetadataHandler = null

    if (curTime > 0) {
      try {
        const dur = player.duration()
        if (!dur || curTime < dur) {
          player.currentTime(curTime)
        }
      } catch (err) {
        console.warn('Could not restore playback time on reset', err)
      }
    }

    try {
      const playPromise = player.play()
      if (playPromise?.catch) {
        playPromise.catch((err) => {
          console.warn('Play refused after reset', err)
        })
      }
    } catch (err) {
      console.warn('Could not start playback on reset', err)
    }

    if (rate) {
      try {
        player.playbackRate(rate)
      } catch (err) {
        console.warn('Could not restore playback rate on reset', err)
      }
    }

    player.beforeReset = null
  }

  player.one('loadedmetadata', player._resetMetadataHandler)

  // Fallback timeout in case loadedmetadata doesn't fire (e.g. offline or hung connection)
  const METADATA_TIMEOUT = 10000 // 10s
  player._resetMetadataTimeout = setTimeout(() => {
    // If we are still waiting for loadedmetadata and offline, leave beforeReset in place so online event can recover
    if (
      player.beforeReset &&
      typeof navigator !== 'undefined' &&
      navigator.onLine === false
    ) {
      return
    }
    // If still in beforeReset state and online, try re-asserting source if still in readyState 0
    if (player.beforeReset && (!player.error || !player.error())) {
      if (
        typeof player.readyState === 'function' &&
        player.readyState() === 0 &&
        curSource
      ) {
        try {
          player.src(curSource)
          const p = player.play()
          if (p?.catch) p.catch(() => null)
        } catch (err) {
          console.warn('Could not re-assert source on timeout', err)
        }
      }
    }
  }, METADATA_TIMEOUT)

  try {
    const p = player.play()
    if (p?.catch) p.catch(() => null)
  } catch (err) {
    console.warn('Initial play failed in resetPlayer', err)
  }
}

// The modern audio player names its control bar children as an object. jQuery caches the parsed
// data-audio-options on the element, so this returns a copy: rewriting the cached object made a
// second setup of the same audio element call .map on an object and throw.
// Tests (helpers.test.mjs): repeated setup, null/undefined, no children, sibling settings.
export const modernAudioOptions = (options) => {
  const children = options?.controlBar?.children
  if (!Array.isArray(children)) return options

  return {
    ...options,
    controlBar: {
      ...options.controlBar,
      children: Object.fromEntries(
        children.map((item) => [
          item,
          item == 'VolumePanel' ? { inline: false } : {},
        ])
      ),
    },
  }
}
